// src/components/ui/NotificationCenter.jsx
import React from "react";
import { Bell, Check, X } from "lucide-react";
import { getNotifications, markNotificationRead, markAllNotificationsRead } from "../../api/lenderPortal/notifications.js";
import toast from "react-hot-toast";

function productCountLabel(count, noun = "product") {
  const n = Number(count) || 0;
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

/** "A lender imported 0 new and 72 updated products" → "Another lender updated 72 products". */
function formatAnotherLenderImport(text, raw = {}) {
  const src = `${text || ""} ${raw.subject || raw.message || ""}`;
  if (!/imported|\bimport\b/i.test(src)) return null;
  if (!/\b(new|updated)\b/i.test(src) && raw.created == null && raw.updated == null) return null;

  const newMatch = String(text || "").match(/(\d+)\s+new/i);
  const updatedMatch = String(text || "").match(/(\d+)\s+updated/i);
  const added = Number(raw.created ?? raw.new ?? raw.products_created ?? (newMatch ? newMatch[1] : 0));
  const updated = Number(raw.updated ?? raw.products_updated ?? (updatedMatch ? updatedMatch[1] : 0));
  const addedN = Number.isFinite(added) ? added : 0;
  const updatedN = Number.isFinite(updated) ? updated : 0;

  const parts = [];
  if (addedN > 0) parts.push(`added new ${productCountLabel(addedN)}`);
  if (updatedN > 0) parts.push(`updated ${productCountLabel(updatedN)}`);
  if (!parts.length) return null;
  return `Another lender ${parts.join(" and ")}`;
}

// Sanitize notification text: reword new-application, lender email → "Another Lender", application ID → "Application", remove "Bulk"
function sanitizeNotificationText(text, raw = {}) {
  if (text == null || typeof text !== "string") return "Notification";
  let out = text.trim() || "Notification";
  // Remove "Bulk" from wording (all notifications)
  out = out.replace(/\bBulk\s*/gi, "").trim();

  const importText = formatAnotherLenderImport(out, raw);
  if (importText) return importText;

  const rawSubject = (raw.subject || raw.message || raw.text || raw.title || "").toLowerCase();
  const isNewApplication = /new\s+application|application\s+created/.test(rawSubject) || /new\s+application|application\s+created/.test(out.toLowerCase());
  let productId = raw.product_id ?? raw.product ?? raw.product_name ?? raw.product_code ?? raw.docname ?? "";
  if (!productId && isNewApplication) {
    const productMatch = out.match(/(?:Product:?\s*)?([A-Za-z]{2,5}-\d{3,})/i);
    if (productMatch) productId = productMatch[1];
  }

  if (isNewApplication && (productId || /new\s+application|application\s+created/.test(out.toLowerCase()))) {
    const displayProductId = typeof productId === "string" ? productId.trim() : String(productId || "—").trim();
    return "New BTL application has been initiated against your product \"" + (displayProductId || "—") + "\"";
  }

  // "Another Lender uploaded X" (number only, no "created")
  const uploadMatch = out.match(/uploaded\s+products?\s*[:\s]*(\d+)|(\d+)\s*created|products?\s*[:\s]*(\d+)/i);
  const uploadCount = raw.count ?? raw.products_count ?? raw.total ?? raw.products_created
    ?? (uploadMatch && (uploadMatch[1] || uploadMatch[2] || uploadMatch[3]));
  if (/uploaded\s+products?|products?\s+uploaded/i.test(out) && uploadCount != null) {
    const num = String(typeof uploadCount === "number" ? uploadCount : (uploadCount || "0")).trim();
    return "Another Lender uploaded " + (num || "0") + " BTL products";
  }

  // Replace explicit lender email (field from backend or inline email)
  const lenderEmail = raw.lender_email ?? raw.lender_email_address ?? raw.email;
  if (lenderEmail && typeof lenderEmail === "string") {
    out = out.replace(new RegExp(lenderEmail.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"), "Another Lender");
  }
  const appId = raw.application_id ?? raw.application ?? raw.reference_id ?? raw.reference;
  if (appId && typeof appId === "string") {
    out = out.replace(new RegExp(appId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"), "Application");
  }
  // Replace any remaining email-like substring
  out = out.replace(/\S+@\S+\.\S+/g, "Another Lender");
  return out;
}

// Helper to format time
function formatTime(dateString) {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  } catch {
    return dateString;
  }
}

export default function NotificationCenter() {
  const [open, setOpen] = React.useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const buttonRef = React.useRef(null);
  const popoverRef = React.useRef(null);

  const unread = items.filter(i => !i.read || i.read === 0).length;

  // Fetch notifications (silent background fetch)
  const loadNotifications = React.useCallback(async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const data = await getNotifications({ limit: 20, unread_only: 0 });
      
      // Normalize notification data from backend
      const normalized = Array.isArray(data) ? data : (data?.notifications || data?.data || []);
      
      setItems(normalized.map(n => {
        const rawText = n.subject || n.message || n.text || n.title || "";
        return {
          id: n.name || n.id || n.notification_id,
          text: sanitizeNotificationText(rawText, n),
          time: formatTime(n.creation || n.created_at || n.date),
          read: n.read === 1 || n.read === true || n.is_read === 1 || n.is_read === true,
          notificationName: n.name || n.notification_id,
          _raw: n,
        };
      }));
    } catch (error) {
      console.error("Failed to load notifications:", error);
      if (showLoading) {
        toast.error("Failed to load notifications");
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  // Load notifications immediately on mount (silent)
  React.useEffect(() => {
    loadNotifications(false);
  }, [loadNotifications]);

  // Refresh silently when opened (don't show loading)
  React.useEffect(() => {
    if (open) {
      loadNotifications(false);
    }
  }, [open, loadNotifications]);

  const markAllRead = async () => {
    try {
      await markAllNotificationsRead();
      await loadNotifications(false); // Silent refresh
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Failed to mark all as read:", error);
      toast.error("Failed to mark all as read");
    }
  };

  const toggleRead = async (item) => {
    if (!item.notificationName) {
      // Fallback: just update local state if no notification name
      setItems(xs => xs.map(x => x.id === item.id ? { ...x, read: !x.read } : x));
      return;
    }

    try {
      if (!item.read) {
        await markNotificationRead(item.notificationName);
        await loadNotifications(false); // Silent refresh
        toast.success("Notification marked as read");
      } else {
        // If already read, we can't mark as unread via API, so just update local state
        setItems(xs => xs.map(x => x.id === item.id ? { ...x, read: false } : x));
      }
    } catch (error) {
      console.error("Failed to toggle read status:", error);
      toast.error("Failed to update notification");
    }
  };

  const clearOne = (id) => {
    // Dismiss is just removing from local state (backend doesn't have delete endpoint)
    setItems(xs => xs.filter(x => x.id !== id));
  };

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        popoverRef.current?.contains(e.target) ||
        buttonRef.current?.contains(e.target)
      ) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className="relative">
      {/* Trigger */}
      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className="relative h-10 w-10 grid place-items-center rounded-xl border hover:bg-gray-50"
        aria-label="Open notifications"
        type="button"
      >
        <Bell className="h-5 w-5 text-gray-700" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-4 rounded-full bg-rose-500 text-white text-[10px] leading-4 px-1 text-center ring-2 ring-white">
            {unread}
          </span>
        )}
      </button>

      {/* Dropdown popover */}
      {open && (
        <div
          ref={popoverRef}
          className="absolute top-full right-0 mt-2 z-[9999] w-[420px] max-w-[90vw] bg-white shadow-2xl rounded-xl border border-slate-200 flex flex-col overflow-hidden"
          style={{ maxHeight: "80vh" }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-sky-600" />
              <h2 className="text-base font-semibold text-gray-900">Notifications</h2>
              {unread > 0 && (
                <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 rounded-full bg-rose-500 text-white text-[10px] font-bold px-1.5">
                  {unread}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={markAllRead}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50 transition-colors"
                title="Mark all as read"
              >
                <Check className="h-3.5 w-3.5" /> Mark all
              </button>
              <button
                onClick={() => setOpen(false)}
                className="h-8 w-8 grid place-items-center rounded-lg border border-slate-300 bg-white hover:bg-slate-50 transition-colors"
                aria-label="Close"
              >
                <X className="h-4 w-4 text-gray-700" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="overflow-auto" style={{ maxHeight: "calc(80vh - 60px)" }}>
            {items.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-500">You're all caught up 🎉</p>
                <p className="text-xs text-gray-400 mt-1">No new notifications</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {items.map(n => (
                  <li
                    key={n.id}
                    className={`px-4 py-3 transition-colors ${
                      n.read ? "bg-white" : "bg-sky-50/50"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`mt-1.5 inline-block h-2.5 w-2.5 rounded-full flex-shrink-0 ${
                          n.read ? "bg-gray-300" : "bg-sky-500"
                        }`}
                        aria-hidden
                      />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${n.read ? "text-gray-700" : "text-gray-900 font-medium"}`}>
                          {n.text}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">{n.time}</p>
                        <div className="mt-2 flex gap-2">
                          <button
                            onClick={() => toggleRead(n)}
                            className="text-xs rounded-lg border border-slate-300 bg-white px-2.5 py-1 hover:bg-slate-50 transition-colors"
                          >
                            {n.read ? "Mark unread" : "Mark read"}
                          </button>
                          <button
                            onClick={() => clearOne(n.id)}
                            className="text-xs rounded-lg border border-slate-300 bg-white px-2.5 py-1 hover:bg-slate-50 transition-colors"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
